import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import {
  FORM_VALID,
  type Validator,
  matchingRegExp,
  maxLength,
  required,
  setFormValues,
} from '@ds/components/Form'

import type { Stakeholder } from '~/api/kyb/types'
import { createStakeholderFormValues } from '~/scopes/kyb/utils/kyb.utils'

import {
  IDENTIFICATION_TYPES,
  VALIDATION_REGEX,
} from '../pages/components/steps/components/StakeholderForm/StakeholderForm.constants'
import stakeholderFormTranslations from '../pages/components/steps/components/StakeholderForm/StakeholderForm.translations'
import type { StakeholderFormValues } from '../pages/components/steps/components/StakeholderForm/StakeholderForm.types'

export function useKybStakeholder() {
  const i18n = useI18n()

  const stakeholderValidationRules = computed(() => {
    return {
      firstName: [
        required(i18n(stakeholderFormTranslations.firstNameRequired)),
        matchingRegExp(
          VALIDATION_REGEX.NAME,
          i18n(stakeholderFormTranslations.invalidFormat),
        ),
        maxLength(
          100,
          i18n(stakeholderFormTranslations.hundredCharactersLimit),
        ),
      ],
      lastName: [
        required(i18n(stakeholderFormTranslations.lastNameRequired)),
        matchingRegExp(
          VALIDATION_REGEX.NAME,
          i18n(stakeholderFormTranslations.invalidFormat),
        ),
        maxLength(
          100,
          i18n(stakeholderFormTranslations.hundredCharactersLimit),
        ),
      ],
      dateOfBirth: [
        required(i18n(stakeholderFormTranslations.dateOfBirthRequired)),
      ],
      countryOfCitizenship: [
        required(
          i18n(stakeholderFormTranslations.countryOfCitizenshipRequired),
        ),
      ],
      phoneNumber: [
        required(i18n(stakeholderFormTranslations.phoneNumberRequired)),
        matchingRegExp(
          VALIDATION_REGEX.PHONE_NUMBER,
          i18n(stakeholderFormTranslations.phoneNumberInvalid),
        ),
      ],
      countryOfResidency: [
        required(i18n(stakeholderFormTranslations.countryOfResidencyRequired)),
      ],
      addressLine1: [
        required(i18n(stakeholderFormTranslations.addressLine1Required)),
        matchingRegExp(
          VALIDATION_REGEX.ADDRESS_LINE,
          i18n(stakeholderFormTranslations.invalidFormat),
        ),
      ],
      addressLine2: [],
      city: [
        required(i18n(stakeholderFormTranslations.cityRequired)),
        matchingRegExp(
          VALIDATION_REGEX.CITY,
          i18n(stakeholderFormTranslations.invalidFormat),
        ),
      ],
      postalCode: [
        required(i18n(stakeholderFormTranslations.postalCodeRequired)),
        matchingRegExp(
          VALIDATION_REGEX.POSTAL_CODE,
          i18n(stakeholderFormTranslations.invalidFormat),
        ),
      ],
      country: [required(i18n(stakeholderFormTranslations.countryRequired))],
      identificationType: [
        required(i18n(stakeholderFormTranslations.identificationTypeRequired)),
      ],
      countryOfIssue: [
        required(i18n(stakeholderFormTranslations.countryOfIssueRequired)),
      ],
      photoIdFront: [
        required(i18n(stakeholderFormTranslations.photoIdFrontRequired)),
      ],
      photoIdBack: [
        ((
          photoIdBack: StakeholderFormValues['photoIdBack'],
          formValues: StakeholderFormValues,
        ) => {
          if (
            formValues.identificationType === IDENTIFICATION_TYPES.NATIONAL_ID
          ) {
            return required(
              i18n(stakeholderFormTranslations.photoIdBackRequired),
            )(photoIdBack, formValues, {})
          }

          return FORM_VALID
        }) as Validator<StakeholderFormValues>,
      ],
    }
  })

  function fillFormAsSameAs(
    formId: string,
    stakeholder: Stakeholder | null | undefined,
    targetField?: string,
  ) {
    if (!stakeholder) return

    const formValues = createStakeholderFormValues(stakeholder)

    if (targetField) {
      setFormValues(formId, { [targetField]: formValues })
    } else {
      setFormValues(formId, formValues)
    }
  }

  return {
    stakeholderValidationRules,
    fillFormAsSameAs,
  }
}
